import {Domain, merge, restore, Store, Event, guard} from 'effector';
import {createGate, Gate} from 'effector-react';

import {rootDomain} from '@app/model';
import {history} from '@shared/lib/history';

import {
    InnerFilters,
    InnerFiltersElement,
    InnerSortings,
    InnerSortingsElement,
} from '@shared/types/grid';

export function createGridTableModel<TGate = any>({
    defaultFilters = {},
    defaultSortings = {},
    domain = rootDomain.createDomain(),
    Gate = createGate<TGate>(),
}: {
    defaultFilters?: InnerFilters;
    defaultSortings?: InnerSortings;
    domain?: Domain;
    Gate?: Gate<TGate>;
}) {
    const filtersChanged = domain.createEvent<InnerFiltersElement[]>('filtersChanged');
    const sortingsChanged = domain.createEvent<InnerSortingsElement[]>('filtersChanged');

    const filtersReset = domain.createEvent<Array<string>>('filtersReset');
    const sortingsReset = domain.createEvent<Array<string>>('sortingsReset');

    const pageNumberChanged = domain.createEvent<number>('pageNumberChanged');
    const pageSizeChanged = domain.createEvent<number>('pageSizeChanged');

    const tableStateChanged = merge([
        sortingsChanged,
        filtersChanged,
        pageNumberChanged,
        pageSizeChanged,
        filtersReset,
        sortingsReset,
    ]);

    const tableStateCacheChanged = merge([
        Gate.close,
        pageSizeChanged,
        filtersChanged,
        filtersReset,
        sortingsChanged,
        sortingsReset,
    ]);

    const filters$ = domain.createStore<InnerFilters>(defaultFilters);
    const sortings$ = domain.createStore<InnerSortings>(defaultSortings);
    const pageNumber$ = restore(pageNumberChanged, 0).reset(filtersChanged, sortingsChanged);
    const pageSize$ = restore(pageSizeChanged, 20);

    const stateReset = domain.createEvent<TGate>('stateReset');

    const shouldReset = () => {
        const queryParams = new URLSearchParams(history.location.search);
        const hasParam = queryParams.has('isBack');
        if (hasParam) {
            queryParams.delete('isBack');
            history.replace({
                search: queryParams.toString(),
            });
        }
        return !hasParam;
    };

    guard({
        clock: Gate.open,
        filter: shouldReset,
        target: stateReset,
    });

    return {
        Gate,
        filters$,
        filtersChanged,
        sortings$,
        sortingsChanged,
        pageNumber$,
        pageNumberChanged,
        pageSize$,
        pageSizeChanged,
        tableStateChanged,
        tableStateCacheChanged,
        defaultFilters,
        defaultSortings,
        filtersReset,
        sortingsReset,
        stateReset,
    };
}

export function createGridListModel({
    defaultFilters = {},
    defaultSortings = {},
    domain = rootDomain.createDomain(),
    Gate = createGate(),
}: {
    defaultFilters?: InnerFilters;
    defaultSortings?: InnerSortings;
    domain?: Domain;
    Gate?: Gate<any>;
}) {
    const filtersChanged = domain.createEvent<InnerFiltersElement[]>('filtersChanged');
    const sortingsChanged = domain.createEvent<InnerSortingsElement[]>('filtersChanged');

    const filtersSubmit = domain.createEvent('filtersApplied');
    const filtersReset = domain.createEvent<Array<string>>('filtersReset');
    const sortingsReset = domain.createEvent<Array<string>>('sortingsReset');

    const pageNumberChanged = domain.createEvent<number>('pageNumberChanged');
    const pageSizeChanged = domain.createEvent<number>('pageSizeChanged');
    const pageIncremented = domain.createEvent('pageIncremented');

    const showConfigBlockChanged = domain.createEvent<boolean>();
    const showConfigBlock$ = restore(showConfigBlockChanged, false);

    const listStateChanged = merge([
        sortingsChanged,
        sortingsReset,
        filtersSubmit,
        filtersReset,
        pageNumberChanged,
        pageSizeChanged,
    ]);

    const filters$ = domain.createStore<InnerFilters>(defaultFilters);
    const sortings$ = domain.createStore<InnerSortings>(defaultSortings);
    const pageNumber$ = restore(pageNumberChanged, 0).reset(filtersChanged, sortingsChanged);
    const pageSize$ = restore(pageSizeChanged, 30);

    const stateReset = domain.createEvent('stateReset');

    const shouldReset = () => {
        const queryParams = new URLSearchParams(history.location.search);
        const hasParam = queryParams.has('isBack');
        if (hasParam) {
            queryParams.delete('isBack');
            history.replace({
                search: queryParams.toString(),
            });
        }
        return !hasParam;
    };

    guard({
        clock: Gate.open,
        filter: shouldReset,
        target: stateReset,
    });

    return {
        Gate,
        filters$,
        filtersChanged,
        filtersSubmit,
        sortings$,
        sortingsChanged,
        pageNumber$,
        pageIncremented,
        pageNumberChanged,
        pageSize$,
        pageSizeChanged,
        listStateChanged,
        defaultFilters,
        defaultSortings,
        filtersReset,
        sortingsReset,
        showConfigBlock$,
        showConfigBlockChanged,
        stateReset,
    };
}

export function initFiltersTable(
    filters: Store<InnerFilters>,
    filtersChanged: Event<InnerFiltersElement[]>,
    filtersReset: Event<string[]>,
    defaultFilters: InnerFilters,
    reset: Event<any>,
) {
    filters
        .on(filtersChanged, (state, payload) => {
            const filters = {...state};
            payload.forEach(({id, value}) => {
                if (value) {
                    filters[id] = value;
                } else {
                    delete filters[id];
                }
            });
            return filters;
        })
        .on(filtersReset, (state, payload) => {
            const filters: InnerFilters = {...state};
            payload.forEach((filter) => {
                if (filter in defaultFilters) {
                    filters[filter] = defaultFilters[filter];
                } else {
                    delete filters[filter];
                }
            });
            return filters;
        })
        .reset(reset);
}

export function initSortingsTable(
    sortings: Store<InnerSortings>,
    sortingsChanged: Event<InnerSortingsElement[]>,
    sortingsReset: Event<string[]>,
    defaultSortings: InnerSortings,
    reset: Event<any>,
) {
    sortings
        .on(sortingsChanged, (_, payload) =>
            payload.reduce((result, {id, value}) => ({...result, [id]: value}), {}),
        )
        .on(sortingsReset, (state, payload) => {
            const update = payload.reduce(
                (result, id) => ({...result, [id]: defaultSortings[id]}),
                {},
            );
            return {...state, ...update};
        })
        .reset(reset);
}

export function initFiltersList(
    filters: Store<InnerFilters>,
    filtersChanged: Event<InnerFiltersElement[]>,
    filtersReset: Event<string[]>,
    reset: Event<any>,
) {
    filters
        .on(filtersChanged, (state, payload) => {
            const filters = {...state};
            payload.forEach(({id, value}) => {
                if (value) {
                    filters[id] = value;
                } else {
                    delete filters[id];
                }
            });
            return filters;
        })
        .reset(filtersReset, reset);
}

export function initSortingsList(
    sortings: Store<InnerSortings>,
    sortingsChanged: Event<InnerSortingsElement[]>,
    sortingsReset: Event<string[]>,
    reset: Event<any>,
) {
    sortings
        .on(sortingsChanged, (_, payload) =>
            payload.reduce((result, {id, value}) => ({...result, [id]: value}), {}),
        )
        .reset(sortingsReset, reset);
}
